import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Alert,
  Spinner,
} from "reactstrap";
import { createStoreProjectItem, getStoreProjectFolders, cloneStoreProjectItem } from "../service";

const ADD_QTY_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

function resolveProductId(product) {
  if (!product) return null;
  const raw =
    product.id ?? product.product_id ?? product.store_product_id ?? null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function StoreQuoteFolderPickerModal({
  isOpen,
  toggle,
  projectId,
  projectName,
  product,
  mode = "add",
  duplicateSourceItem = null,
  onAdded,
  onError,
  externalBusy = false,
}) {
  const isDuplicateMode = mode === "duplicate" && duplicateSourceItem != null;
  const [currentParentId, setCurrentParentId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [folders, setFolders] = useState([]);
  const [recentFolders, setRecentFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addingHere, setAddingHere] = useState(false);
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [addQty, setAddQty] = useState(1);

  const resetNavigation = useCallback(() => {
    const name = String(projectName ?? "").trim();
    setCurrentParentId(null);
    setBreadcrumb([{ id: null, name }]);
    setShowNewFolderForm(false);
    setNewFolderName("");
    setRecentFolders([]);
    setError(null);
    setAddQty(1);
  }, [projectName]);

  useEffect(() => {
    if (!isOpen) return;
    resetNavigation();
  }, [isOpen, projectId, resetNavigation]);

  const loadFolders = useCallback(async () => {
    if (!projectId || !isOpen) return;
    setLoading(true);
    setError(null);

    try {
      const body = await getStoreProjectFolders(projectId, {
        parentId: currentParentId,
      });
      setFolders(body.data.folders);
      setRecentFolders(
        Array.isArray(body.data.recent_folders) ? body.data.recent_folders : []
      );
    } catch (e) {
      setError("Unable to load folders.");
    } finally {
      setLoading(false);
    }
  }, [projectId, isOpen, currentParentId]);

  useEffect(() => {
    if (!isOpen || breadcrumb.length === 0) return;
    void loadFolders();
  }, [isOpen, breadcrumb.length, loadFolders]);

  const busy = externalBusy || addingHere || creatingFolder;

  const addProductToFolder = async (parentId) => {
    if (!projectId) return;

    setAddingHere(true);
    setError(null);

    try {
      let lastClonedId = null;

      if (isDuplicateMode) {
        const sourceItemId = duplicateSourceItem?.id;
        if (sourceItemId == null) return;

        for (let i = 0; i < addQty; i += 1) {
          const res = await cloneStoreProjectItem(projectId, sourceItemId, {
            parentId: parentId ?? null,
          });
          const newId = res?.data?.id;
          if (newId != null) lastClonedId = newId;
        }

        onAdded?.({
          projectId,
          parentId: parentId ?? null,
          clonedItemId: lastClonedId,
          quantity: addQty,
          mode: "duplicate",
        });
        toggle();
        return;
      }

      const productId = resolveProductId(product);
      if (!productId) return;

      for (let i = 0; i < addQty; i += 1) {
        await createStoreProjectItem(projectId, {
          item_type: "product",
          product_id: productId,
          ...(parentId != null ? { parent_id: parentId } : {}),
        });
      }
      onAdded?.({
        projectId,
        parentId: parentId ?? null,
        productId,
        quantity: addQty,
        mode: "add",
      });
      toggle();
    } catch (e) {
      const message = isDuplicateMode
        ? "Unable to duplicate product."
        : "Unable to add product.";
      setError(message);
      onError?.(message);
    } finally {
      setAddingHere(false);
    }
  };

  const navigateToBreadcrumbIndex = (index) => {
    if (index < 0 || index >= breadcrumb.length) return;

    const next = breadcrumb.slice(0, index + 1);
    const target = next[next.length - 1];
    setBreadcrumb(next);
    setCurrentParentId(target?.id ?? null);
    setShowNewFolderForm(false);
    setNewFolderName("");
  };

  const openFolder = (folder) => {
    const id = folder?.id;
    const name = String(folder?.name ?? "").trim() || "Folder";

    if (id == null) return;

    setBreadcrumb((prev) => [...prev, { id, name }]);
    setCurrentParentId(id);
    setShowNewFolderForm(false);
    setNewFolderName("");
  };

  const handleCreateFolder = async (e) => {
    e?.preventDefault?.();
    const name = String(newFolderName ?? "").trim();

    if (!name || !projectId) return;

    setCreatingFolder(true);
    setError(null);

    try {
      await createStoreProjectItem(projectId, {
        item_type: "folder",
        name,
        ...(currentParentId != null ? { parent_id: currentParentId } : {}),
      });
      setNewFolderName("");
      setShowNewFolderForm(false);
      await loadFolders();
    } catch (e) {
      setError("Unable to create folder.");
    } finally {
      setCreatingFolder(false);
    }
  };

  const latestRecentFolder =
    recentFolders.length > 0 ? recentFolders[0] : null;

  const headerToggle = () => {
    if (busy) return;
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={headerToggle}
      centered
      size="md"
      className="store-quote-folder-picker-modal"
    >
      <ModalHeader toggle={headerToggle} className="border-0 pb-0">
        <span className="visually-hidden">Choose folder</span>
      </ModalHeader>
      <ModalBody className="pt-2">
        {error ? (
          <Alert color="danger" className="py-2 small mb-3">
            {error}
          </Alert>
        ) : null}

        {showNewFolderForm ? (
          <form
            className="mb-3 p-3 border rounded bg-light"
            onSubmit={handleCreateFolder}
          >
            <Label
              for="store-quote-new-folder-name"
              className="small fw-semibold"
            >
              Folder name
            </Label>
            <div className="d-flex gap-2 mt-1">
              <Input
                id="store-quote-new-folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="New folder"
                disabled={busy}
                autoFocus
              />
              <Button
                color="primary"
                type="submit"
                disabled={busy || !newFolderName.trim()}
              >
                {creatingFolder ? <Spinner size="sm" /> : "Create"}
              </Button>
              <Button
                color="secondary"
                outline
                type="button"
                disabled={busy}
                onClick={() => {
                  setShowNewFolderForm(false);
                  setNewFolderName("");
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : null}

        {latestRecentFolder ? (
          <div className="mb-2 p-2 border rounded bg-light">
            <div className="small text-muted mb-2">Recent folder</div>
            <div className="d-flex align-items-center gap-2 store-quote-folder-picker-modal__recent-row">
              <i
                className="fa-solid fa-folder text-secondary flex-shrink-0"
                aria-hidden
              />
              <span className="small text-dark flex-grow-1 min-w-0">
                {latestRecentFolder.path_label}
              </span>
              <Button
                color="outline-primary"
                size="sm"
                className="rounded-pill px-2 text-nowrap flex-shrink-0"
                disabled={busy}
                onClick={() => void addProductToFolder(latestRecentFolder.id)}
              >
                Add Here
              </Button>
            </div>
          </div>
        ) : null}

        <nav
          className="small mb-2 store-quote-folder-picker-modal__breadcrumb"
          aria-label="Folder path"
        >
          {breadcrumb.map((crumb, index) => (
            <span key={`${crumb.id ?? "root"}-${index}`}>
              {index > 0 ? (
                <span className="text-muted mx-1" aria-hidden>
                  &gt;
                </span>
              ) : null}
              {index < breadcrumb.length - 1 ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline text-decoration-none"
                  disabled={busy}
                  onClick={() => navigateToBreadcrumbIndex(index)}
                >
                  {crumb.name}
                </button>
              ) : (
                <span className="fw-semibold text-dark">{crumb.name}</span>
              )}
            </span>
          ))}
        </nav>

        <div
          className="store-quote-folder-picker-modal__list border rounded scrollbar"
          style={{ maxHeight: "min(360px, 50vh)" }}
        >
          <div className="list-group list-group-flush">
            {loading ? (
              <div className="list-group-item text-center py-4 text-muted">
                <Spinner size="sm" className="me-2" />
                Loading folders…
              </div>
            ) : folders.length === 0 ? (
              <div className="list-group-item py-3 small text-muted">
                No folders here yet. Use Add Folder below to create one.
              </div>
            ) : (
              folders.map((folder) => (
                <div
                  key={folder.id}
                  className="list-group-item d-flex align-items-center gap-2 p-2 store-quote-folder-picker-modal__row"
                >
                  <i
                    className="fa-solid fa-folder text-secondary"
                    aria-hidden
                  />
                  <button
                    type="button"
                    className="btn btn-link p-0 text-start flex-grow-1 text-decoration-none"
                    disabled={busy}
                    onClick={() => openFolder(folder)}
                  >
                    {folder.name}
                    {Number(folder.children_folder_count) > 0 ? (
                      <i
                        className="fa-solid fa-chevron-right ms-2 small"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                  <Button
                    color="outline-primary"
                    size="sm"
                    className="rounded-pill px-2 text-nowrap"
                    disabled={busy}
                    onClick={() => void addProductToFolder(folder.id)}
                  >
                    Add Here
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-2">
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none text-nowrap"
            disabled={busy}
            onClick={() => setShowNewFolderForm((v) => !v)}
          >
            <i className="fa-solid fa-folder-plus me-1" aria-hidden />
            Add Folder
          </button>
        </div>
      </ModalBody>
      <ModalFooter className="bg-light d-flex justify-content-between align-items-center w-100">
        <div className="d-flex align-items-center gap-2">
          <Label for="store-quote-add-qty" className="small mb-0 text-nowrap">
            Qty:
          </Label>
          <Input
            id="store-quote-add-qty"
            type="select"
            bsSize="sm"
            className="w-auto"
            value={addQty}
            disabled={busy}
            onChange={(e) => setAddQty(Number(e.target.value))}
          >
            {ADD_QTY_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Input>
        </div>
        {isDuplicateMode ? (
          <Button
            color="primary"
            size="sm"
            className="rounded-pill px-3 text-nowrap"
            disabled={busy}
            onClick={() =>
              void addProductToFolder(duplicateSourceItem.parent_id ?? null)
            }
          >
            {addingHere ? <Spinner size="sm" /> : "Add to current folder"}
          </Button>
        ) : (
          currentParentId == null && (
            <Button
              color="primary"
              size="sm"
              className="rounded-pill px-3 text-nowrap"
              disabled={busy}
              onClick={() => void addProductToFolder(null)}
            >
              {addingHere ? <Spinner size="sm" /> : "Add without Folder"}
            </Button>
          )
        )}
      </ModalFooter>
    </Modal>
  );
}

export default StoreQuoteFolderPickerModal;
